// src/pages/apikey/ApikeyPoliciesPage.tsx
// API key policies (admin) — org-wide limits on who can create keys and how.
// See docs/ux/apikey-ia.md for the full RPC placement.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useApikeyApiKeyPolicy, useI18n } from '@react/hooks';
import {
  Button, Card, ErrorAlert, Input,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '@react/components';
import { PolicyForm } from '@/features/apikey/PolicyForm';
import { TypedConfirmDialog } from '@/features/apikey/TypedConfirmDialog';
import type { APIKeyPolicy } from '@core/types/apikey';
import {
  policyFormFromPolicy, policyFormToRequestFields, deletePolicyConfirmPhrase,
} from '@/features/apikey/logic';
import type { PolicyFormState } from '@/features/apikey/logic';

function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (<svg className={`${className ?? ''} transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>);
}

function PolicyRow({ policy, onEdit, onDelete, onResolve }: {
  policy: APIKeyPolicy;
  onEdit: () => void;
  onDelete: () => void;
  onResolve: () => void;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow>
        <TableCell>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-2 text-left w-full"
          >
            <ChevronIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" open={expanded} />
            <span className="text-sm font-medium text-foreground">{policy.name}</span>
          </button>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">{policy.scope_entity_type || t('apikey.policy.anyScope')}</TableCell>
        <TableCell className="text-sm text-muted-foreground">{policy.max_keys ?? '—'}</TableCell>
        <TableCell className="text-sm text-muted-foreground">{policy.max_ttl_days ?? '—'}</TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={onResolve}>{t('apikey.policy.resolve')}</Button>
            <Button variant="ghost" size="sm" onClick={onEdit}>{t('common.edit')}</Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete}>{t('common.delete')}</Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/20 px-6 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">{t('apikey.policy.field.maxKeysPerUser')}: </span>{policy.max_keys_per_user ?? '—'}</div>
              <div><span className="text-muted-foreground">{t('apikey.policy.field.defaultTtlDays')}: </span>{policy.default_ttl_days ?? '—'}</div>
              <div><span className="text-muted-foreground">{t('apikey.policy.field.maxRequestsPerMinute')}: </span>{policy.max_requests_per_minute ?? '—'}</div>
              <div><span className="text-muted-foreground">{t('apikey.policy.field.allowNeverExpires')}: </span>{policy.allow_never_expires ? t('common.yes') : t('common.no')}</div>
              <div><span className="text-muted-foreground">{t('apikey.policy.field.requireExpiration')}: </span>{policy.require_expiration ? t('common.yes') : t('common.no')}</div>
              <div><span className="text-muted-foreground">{t('apikey.policy.field.requireIpRestrictions')}: </span>{policy.require_ip_restrictions ? t('common.yes') : t('common.no')}</div>
              <div className="col-span-full">
                <span className="text-muted-foreground">{t('apikey.policy.field.allowedPermissionSections')}: </span>
                {policy.allowed_permission_sections.length > 0 ? policy.allowed_permission_sections.join(', ') : t('apikey.policy.allSections')}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export const ApikeyPoliciesPage = observer(() => {
  const store = useApikeyApiKeyPolicy();
  const { t } = useI18n();

  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<APIKeyPolicy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<APIKeyPolicy | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [lookupEntityType, setLookupEntityType] = useState('');
  const [lookupEntityId, setLookupEntityId] = useState('');
  const [resolved, setResolved] = useState<APIKeyPolicy | null>(null);

  const reload = useCallback(() => {
    store.ListAPIKeyPolicies({ pagination: { cursor: '', limit: 100, direction: 'forward' }, sort: [], view: 0 });
  }, [store]);

  useEffect(() => { reload(); }, [reload]);

  const handleCreate = async (data: PolicyFormState) => {
    setIsActing(true);
    try {
      await store.CreateAPIKeyPolicy(policyFormToRequestFields(data) as never);
      setShowCreate(false);
      reload();
    } finally {
      setIsActing(false);
    }
  };

  const handleUpdate = async (data: PolicyFormState) => {
    if (!editTarget?.entity?.id) return;
    setIsActing(true);
    try {
      await store.UpdateAPIKeyPolicy(editTarget.entity.id, {
        policy: policyFormToRequestFields(data) as never,
        field_mask: Object.keys(policyFormToRequestFields(data)),
      });
      setEditTarget(null);
      reload();
    } finally {
      setIsActing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    setIsActing(true);
    try {
      await store.DeleteAPIKeyPolicy(deleteTarget.entity.id);
      setDeleteTarget(null);
      reload();
    } finally {
      setIsActing(false);
    }
  };

  const handleLookup = async (policy: APIKeyPolicy) => {
    if (!policy.scope_entity_type) return;
    // GetAPIKeyPolicyByEntity looks up the policy directly attached to an entity — demoed
    // against the policy's own scope type since we don't have a live entity picker here.
    try {
      await store.GetAPIKeyPolicyByEntity(policy.scope_entity_type, policy.entity?.id ?? '');
    } catch {
      // shown via store.error
    }
  };

  const handleResolveLookup = async () => {
    if (!lookupEntityType || !lookupEntityId) return;
    try {
      const resp = await store.ResolveAPIKeyContext({
        scope_entity: { type: lookupEntityType, id: lookupEntityId } as never,
        scope_chain: [],
      });
      setResolved(resp.effective_policy ?? null);
    } catch {
      setResolved(null);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('apikey.policy.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('apikey.policy.subtitle')}</p>
          <Link to="/apikeys" className="text-sm text-primary hover:underline">{t('apikey.policy.backToKeys')}</Link>
        </div>
        {!showCreate && (
          <Button onClick={() => setShowCreate(true)}>{t('apikey.policy.create')}</Button>
        )}
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {showCreate && (
        <PolicyForm
          title={t('apikey.policy.create')}
          submitLabel={t('common.create')}
          isSaving={isActing}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {editTarget && (
        <PolicyForm
          title={t('apikey.policy.editTitle')}
          submitLabel={t('common.save')}
          initial={policyFormFromPolicy(editTarget)}
          isSaving={isActing}
          onSubmit={handleUpdate}
          onCancel={() => setEditTarget(null)}
        />
      )}

      <TypedConfirmDialog
        open={!!deleteTarget}
        title={t('apikey.policy.deleteTitle')}
        description={t('apikey.policy.deleteDescription')}
        phrase={deleteTarget ? deletePolicyConfirmPhrase(deleteTarget) : ''}
        confirmLabel={t('common.delete')}
        isConfirming={isActing}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Resolve effective policy for an arbitrary scope entity */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold text-foreground mb-2">{t('apikey.policy.resolveTitle')}</h2>
        <p className="text-xs text-muted-foreground mb-3">{t('apikey.policy.resolveHelp')}</p>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            label={t('apikey.policy.resolveEntityType')}
            value={lookupEntityType}
            onChange={(e) => setLookupEntityType(e.target.value)}
            placeholder="team"
            fullWidth={false}
          />
          <Input
            label={t('apikey.policy.resolveEntityId')}
            value={lookupEntityId}
            onChange={(e) => setLookupEntityId(e.target.value)}
            fullWidth={false}
          />
          <Button variant="secondary" onClick={handleResolveLookup}>{t('apikey.policy.resolve')}</Button>
        </div>
        {resolved && (
          <p className="mt-3 text-sm text-foreground">
            {t('apikey.policy.resolvedResult', { name: resolved.name })}
          </p>
        )}
      </Card>

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('apikey.policy.column.name')}</TableHead>
              <TableHead>{t('apikey.policy.column.scope')}</TableHead>
              <TableHead>{t('apikey.policy.column.maxKeys')}</TableHead>
              <TableHead>{t('apikey.policy.column.maxTtl')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={5} message={t('apikey.policy.empty')} />
            ) : (
              store.items.map((policy) => (
                <PolicyRow
                  key={policy.entity?.id}
                  policy={policy}
                  onEdit={() => {
                    // GetAPIKeyPolicy — the authoritative single-policy read, in case the list row is stale.
                    const id = policy.entity?.id;
                    if (id) store.GetAPIKeyPolicy(id).then(setEditTarget).catch(() => setEditTarget(policy));
                    else setEditTarget(policy);
                  }}
                  onDelete={() => setDeleteTarget(policy)}
                  onResolve={() => handleLookup(policy)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default ApikeyPoliciesPage;
